// Dynamically load the Google Maps JS API (Places library) — but only when a
// real API key is configured at build time. Loading the script with an empty
// or placeholder key makes Google render its blocking "This page can't load
// Google Maps correctly" error dialog. When no key is present we simply skip
// loading; the booking flow degrades gracefully to manual address entry.

let loadPromise: Promise<boolean> | null = null

function hasUsableKey(key: string | undefined): key is string {
  if (!key) return false
  const trimmed = key.trim()
  // Guard against an unresolved Vite placeholder (e.g. "%VITE_...%") or empty.
  return trimmed !== '' && !trimmed.includes('%VITE')
}

export function loadGoogleMaps(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false)
  }
  if (loadPromise) return loadPromise

  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
  if (!hasUsableKey(key)) {
    loadPromise = Promise.resolve(false)
    return loadPromise
  }

  // Already present (e.g. injected by a prior call or SSR hydration).
  if ((window as unknown as { google?: { maps?: unknown } }).google?.maps) {
    loadPromise = Promise.resolve(true)
    return loadPromise
  }

  loadPromise = new Promise<boolean>((resolve) => {
    const existing = document.getElementById('gmaps-js') as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => resolve(true))
      existing.addEventListener('error', () => resolve(false))
      return
    }
    const script = document.createElement('script')
    script.id = 'gmaps-js'
    script.async = true
    script.defer = true
    script.src =
      'https://maps.googleapis.com/maps/api/js?key=' +
      encodeURIComponent(key) +
      '&libraries=places&loading=async'
    script.addEventListener('load', () => resolve(true))
    script.addEventListener('error', () => resolve(false))
    document.head.appendChild(script)
  })

  return loadPromise
}
